import {
	Component,
	Input,
	Output,
	ViewChild,
	EventEmitter,
	ChangeDetectionStrategy,
	OnInit,
	inject,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Pin, PinsResult } from '@models/teranet';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SalePinActions, SalePinState } from '@state/sale_pins';
import { PurchaseStates, PurchaseStateType } from '@state/utils/models';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { TestHooks } from './pins-table.hooks';
import { BaseComponent } from '@core/base.component';
import { Observable, takeUntil } from 'rxjs';
import { Select } from '@ngxs/store';
import { SalesPinsDispatch } from '@state/sale_pins/salesPins.dispatch';
import { TeranetService } from '@services/teranet/teranet.service';

interface VM {
	showContinueMessage: boolean;
	pins: PinsResult;
	dataSource: MatTableDataSource<Pin>;
	purchaseDeleted: boolean;
	parcelPurchaseState: PurchaseStates;
	currentPurchasedPin: string;
	toastSyncText: string;
}

const defaultVM = {
	showContinueMessage: false,
	pins: null,
	dataSource: null,
	purchaseDeleted: true,
	parcelPurchaseState: null,
	currentPurchasedPin: null,
	toastSyncText: '',
};

enum PurchaseActionState {
	UNPURCHASED = 'unpurchased',
	PURCHASING = 'purchasing',
	PURCHASED = 'purchased',
}

@Component({
	selector: 'app-pins-table',
	templateUrl: './pins-table.component.html',
	styleUrls: ['./pins-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class PinsTableComponent extends BaseComponent<VM> implements OnInit {
	testHooks = TestHooks;
	public purchaseState = PurchaseStateType;
	public purchaseActionState = PurchaseActionState;
	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;
	@SelectSnapshot(SalePinState.getPurchased) purchasedPins;
	@Select(SalePinState.getParcelPurchaseState)
	parcelPurchaseState$: Observable<PurchaseStates>;

	salesPinsDispatch = inject(SalesPinsDispatch);
	teranetService = inject(TeranetService);

	@ViewChild(MatSort) sort: MatSort;
	@Input() searching = false;

	@Input() error: string;
	@Input() searchingByOwners = false;

	@Input() name: string;
	@Input() isOverview = false;

	@Input()
	set pins(_pins: PinsResult) {
		if (_pins) {
			const dataSource = new MatTableDataSource(_pins.pin_infos);
			dataSource.sort = this.sort;

			this.VM = {
				pins: _pins,
				dataSource,
			};
		}
	}

	@Output() onPurchasePin = new EventEmitter<Pin>();

	public dataSource;
	public displayedColumns: string[] = [
		'pin',
		'registrationType',
		'status',
		'address',
		'legalDesc',
		'purchase',
	];

	constructor() {
		super(defaultVM);
	}

	ngOnInit() {
		this.parcelPurchaseState$
			.pipe(takeUntil(this.destroy$))
			.subscribe((parcelPurchaseState) => {
				this.VM = {
					parcelPurchaseState,
				};

				if (
					parcelPurchaseState[this.VM.currentPurchasedPin]?.state ===
					PurchaseStateType.SUCCESSFUL
				) {
					this.selectPin(this.VM.currentPurchasedPin);

					this.VM = {
						currentPurchasedPin: null,
					};
				}
			});
	}

	applyFilter = (filterValue: string): void => {
		filterValue = filterValue.trim();
		filterValue = filterValue.toLowerCase();
		this.VM = {
			dataSource: {
				...this.VM.dataSource,
				filter: filterValue,
			} as MatTableDataSource<Pin>,
		};
	};

	trackByUid = (index) => index;

	@Dispatch()
	_purchasePin(pin: Pin, purchaseDeleted: boolean) {
		return new SalePinActions.PurchaseParcel({
			pin,
			include_deleted: purchaseDeleted,
			parent: true,
		});
	}

	purchasePin(pin: Pin) {
		this.VM = {
			currentPurchasedPin: pin.pin,
		};

		this._purchasePin(pin, this.VM.purchaseDeleted);
	}

	selectPin(pin: string) {
		this.salesPinsDispatch.selectPin(pin);
	}

	pinPurchased = (pin: string): boolean =>
		this.purchasedPins.some((x) => x.pin === pin);

	pinPurchaseActionState = (pin: string): PurchaseActionState => {
		const pinState = this.VM.parcelPurchaseState[pin]?.state;

		if (pinState === PurchaseStateType.PURCHASING) {
			return PurchaseActionState.PURCHASING;
		} else {
			if (this.pinPurchased(pin)) {
				this.VM = {
					showContinueMessage: !this.isOverview,
				};

				return PurchaseActionState.PURCHASED;
			} else {
				return PurchaseActionState.UNPURCHASED;
			}
		}
	};
}
